// Review what the reason pass proposed, then apply it.
//
// ── WHY THERE IS A REVIEW STEP AT ALL ────────────────────────────────────────
// The endpoint behind this writes nothing; applying is a second call. That is not
// caution for its own sake. The point of a controlled vocabulary is that failures
// become countable, and a vocabulary that grows itself ends up holding FAILED
// FLASH, FLASH FAILED and BAD FLASH as three separate codes counting one thing.
// A human glancing at the new codes once per manifest is the merge step.
//
// So the NEW codes are what this panel puts first and largest. Matches onto
// existing codes are the safe majority and are listed compactly below them.

import React, { useMemo, useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Dialog from '../../components/ui/Dialog';
import { Tooltip } from '../../components/ui/Tooltip';
import type { NormalizeProposal } from './boardReturnsApi';

interface Props {
  proposals: NormalizeProposal[];
  newReasons: { code: string; label: string; description: string | null }[];
  skipped: { n: number | undefined; why: string; code?: string }[];
  applying: boolean;
  onApply: (accepted: NormalizeProposal[]) => void;
  onClose: () => void;
}

const SKIP_COPY: Record<string, string> = {
  no_such_line: 'answered for a line that is not on this manifest',
  invalid_code: 'proposed a code that will not fit a label',
  new_code_undeclared: 'used a new code without defining it',
};

const ReasonPassModal: React.FC<Props> = ({
  proposals,
  newReasons,
  skipped,
  applying,
  onApply,
  onClose,
}) => {
  // Everything starts accepted. The common case is that the pass is right, and
  // making somebody tick forty boxes to agree with it would cost more than the
  // typing it saved.
  const [rejected, setRejected] = useState<Set<string>>(new Set());

  const toggle = (lineId: string) => {
    setRejected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const accepted = useMemo(
    () => proposals.filter((p) => !rejected.has(p.line_id)),
    [proposals, rejected],
  );

  const newlyMinted = useMemo(
    () => new Set(accepted.filter((p) => p.is_new).map((p) => p.code)),
    [accepted],
  );

  const newProposals = proposals.filter((p) => p.is_new);
  const matchedProposals = proposals.filter((p) => !p.is_new);

  const row = (p: NormalizeProposal) => {
    const off = rejected.has(p.line_id);
    return (
      <Checkbox
        key={p.line_id}
        fill
        className={`px-3 py-2 border-t border-outline-variant transition-colors ${off ? 'opacity-40' : 'hover:bg-surface-container'}`}
        checked={!off}
        onChange={() => toggle(p.line_id)}
        label={(
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-sm text-on-surface">{p.label_identity}</span>
            <span className="font-mono text-xs text-primary">{p.code}</span>
            {p.confidence !== null && (
              <span className="text-[11px] text-on-surface-variant tabular-nums">
                {Math.round(p.confidence * 100)}%
              </span>
            )}
          </span>
        )}
        description={(
          <>
            <Tooltip label={p.reason_raw} truncated>
              <span className="block truncate">{p.reason_raw}</span>
            </Tooltip>
            {p.note && <span className="block text-[11px] mt-0.5">{p.note}</span>}
          </>
        )}
      />
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      busy={applying}
      dirty={rejected.size > 0}
      size="lg"
      title="Reason pass"
      meta={<span className="tabular-nums">{proposals.length} line{proposals.length === 1 ? '' : 's'} read</span>}
      footer={(
        <>
          <span className="mr-auto text-xs text-on-surface-variant">
            {accepted.length === 0
              ? 'Nothing selected'
              : `${accepted.length} line${accepted.length === 1 ? '' : 's'} will be coded`}
            {newlyMinted.size > 0 && `, ${newlyMinted.size} new code${newlyMinted.size === 1 ? '' : 's'}`}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onApply(accepted)}
            disabled={applying || accepted.length === 0}
            className={buttonClass('filled')}
          >
            {applying ? 'Applying' : 'Apply'}
          </button>
        </>
      )}
    >
      {newProposals.length > 0 && (
        <section>
          <div className="pt-2 pb-2">
            <h3 className="text-sm font-semibold text-on-surface">
              New codes, added to the vocabulary
            </h3>
            <p className="mt-1 text-xs text-on-surface-variant">
              These become permanent options for every future manifest. Uncheck anything that
              duplicates a code you already have.
            </p>
          </div>
          <div className="mb-4 border border-primary/30 bg-primary/5 rounded">
            {newReasons
              .filter((r) => newlyMinted.has(r.code) || newProposals.some((p) => p.code === r.code))
              .map((r) => (
                <div key={r.code} className="px-3 py-2 border-b border-primary/20 last:border-b-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm text-primary">{r.code}</span>
                    <span className="text-sm text-on-surface">{r.label}</span>
                    {!newlyMinted.has(r.code) && (
                      <span className="text-[11px] text-on-surface-variant">not being added</span>
                    )}
                  </div>
                  {r.description && (
                    <p className="mt-0.5 text-xs text-on-surface-variant">{r.description}</p>
                  )}
                </div>
              ))}
          </div>
          <div className="mb-5 rounded bg-gray-900/60 overflow-hidden">
            {newProposals.map(row)}
          </div>
        </section>
      )}

      {matchedProposals.length > 0 && (
        <section className="pb-4">
          <div className="pt-2 pb-2">
            <h3 className="text-sm font-semibold text-on-surface">Matched to existing codes</h3>
          </div>
          <div className="rounded bg-gray-900/60 overflow-hidden">
            {matchedProposals.map(row)}
          </div>
        </section>
      )}

      {skipped.length > 0 && (
        <section>
          {/* Said out loud rather than dropped quietly: a line that silently
              stays unreasoned looks like the pass simply missed it. */}
          <div className="rounded bg-gray-900/60 p-3">
            <h3 className="text-xs uppercase tracking-wider text-on-surface-variant">
              Left alone
            </h3>
            <ul className="mt-1.5 space-y-1">
              {skipped.map((s, i) => (
                <li key={i} className="text-xs text-on-surface-variant">
                  Line {s.n ?? '?'}: {SKIP_COPY[s.why] || s.why}
                  {s.code && <span className="font-mono text-on-surface-variant"> ({s.code})</span>}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </Dialog>
  );
};

export default ReasonPassModal;
